import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCategories } from '@/lib/supabase';
import type { Category } from '@/lib/supabase';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface CategorySelectProps {
  value?: Category[] | null;
  onChange: (categories: Category[]) => void;
}

const CategorySelect: React.FC<CategorySelectProps> = ({ value = [], onChange }) => {
  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: getCategories
  });

  const handleSelect = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    if (category && !value?.find(v => v.id === category.id)) {
      onChange([...(value || []), category]);
    }
  };

  const handleRemove = (categoryId: string) => {
    onChange((value || []).filter(c => c.id !== categoryId));
  };

  return (
    <div className="space-y-2">
      <Select onValueChange={handleSelect}>
        <SelectTrigger>
          <SelectValue placeholder="Add categories" />
        </SelectTrigger>
        <SelectContent>
          {categories.map((category) => (
            <SelectItem 
              key={category.id} 
              value={category.id}
              disabled={value?.some(v => v.id === category.id)}
            >
              {category.title}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="flex flex-wrap gap-2">
        {value?.map((category) => (
          <Badge 
            key={category.id}
            variant="secondary"
            className="pl-2 pr-1 py-1 flex items-center gap-1"
          >
            {category.title}
            <Button
              variant="ghost"
              size="icon"
              className="h-4 w-4 hover:bg-transparent"
              onClick={() => handleRemove(category.id)}
            >
              <X size={12} />
            </Button>
          </Badge>
        ))}
      </div>
    </div>
  );
};

export default CategorySelect;